import Store from '@/store'
import { CallbackWrap } from '@/types/common'
import { Embed } from '@/types/embed'
import { LevelingEmbed, LevelingTabDefinition } from '@/types/leveling'
import { getUniqueId, Tabs } from '@maxroll-media-group/common-libs'
import classNames from 'classnames'
import React from 'react'
import './leveling.scss'
import { CustomTab } from './tabs/custom-tab'
import { LevelingTab } from './tabs/leveling-tab'
import { Overview } from './tabs/overview'
import { ProgressionTab } from './tabs/progression-tab'
import { LevelingTabItem } from './widgets/leveling-tab-item'

export function Tab({
  tab,
  index,
  tabsAttr,
  levelOverride,
  onLevelChange,
  embed,
  onEmbedChange,
  stores,
  metadataId,
  isActiveTab,
  setTab
}: {
  tab: Omit<LevelingTabDefinition, 'name'>
  index: number
  tabsAttr: 'tabs' | 'extraTabs'
  levelOverride?: number
  onLevelChange?: (level: number) => void
  embed: LevelingEmbed
  onEmbedChange?: (value: CallbackWrap<Embed>) => void
  stores: Record<string, Store>
  metadataId: string
  isActiveTab?: boolean
  setTab: (id: string) => void
}) {
  return (
    <>
      {tab.id === 'overview' && (
        <Overview embed={embed} onEmbedChange={onEmbedChange} stores={stores} isActiveTab={isActiveTab} />
      )}
      {tab.id === 'leveling' && (
        <LevelingTab
          embed={embed}
          onEmbedChange={onEmbedChange}
          stores={stores}
          levelOverride={levelOverride}
          onLevelChange={onLevelChange}
          metadataId={metadataId}
          isActiveTab={isActiveTab}
          onLevelingCompleted={() => {
            setTab('progression')
          }}
        />
      )}
      {tab.id === 'progression' && (
        <ProgressionTab
          embed={embed}
          onEmbedChange={onEmbedChange}
          stores={stores}
          metadataId={metadataId}
          activeTabIndex={index}
          isActiveTab={isActiveTab}
        />
      )}
      {!['overview', 'leveling', 'progression', 'new'].includes(tab.id) && (
        <CustomTab
          embed={embed}
          tabsAttr={tabsAttr}
          onEmbedChange={onEmbedChange}
          tabIndex={index}
          isActiveTab={isActiveTab}
        />
      )}
    </>
  )
}

export function Leveling({
  embed,
  stores,
  levelOverride,
  onLevelChange,
  onEmbedChange,
  metadataId
}: {
  embed: LevelingEmbed
  stores: Record<string, Store>
  levelOverride?: number
  onLevelChange?: (level: number) => void
  onEmbedChange?: (value: CallbackWrap<Embed>) => void
  metadataId: string
}) {
  const [tab, setTab] = React.useState(embed.tabs[0]?.id ?? '')
  const activeTabIndex = React.useMemo(() => {
    const index = embed.tabs.findIndex((item) => item.id === tab)
    return index === -1 ? undefined : index
  }, [embed.tabs, tab])
  const activeExtraTabIndex = React.useMemo(() => {
    const index = embed.extraTabs.findIndex((item) => item.id === tab)
    return index === -1 ? undefined : index
  }, [embed.extraTabs, tab])

  const tabs = React.useMemo(() => {
    if (!onEmbedChange) return embed.tabs

    return [
      ...embed.tabs.map((item, index) => ({
        ...item,
        name: (
          <LevelingTabItem
            embed={embed}
            onEmbedChange={onEmbedChange}
            tab={item}
            setTab={setTab}
            tabsAttr="tabs"
            tabIndex={index}
          />
        )
      })),
      {
        id: 'new',
        name: <span style={{ fontSize: '26px', lineHeight: '20px' }}>+</span>
      }
    ]
  }, [embed, onEmbedChange])

  const extraTabs = React.useMemo(() => {
    if (!onEmbedChange) return embed.extraTabs

    return [
      ...embed.extraTabs.map((item, index) => ({
        ...item,
        name: (
          <LevelingTabItem
            embed={embed}
            onEmbedChange={onEmbedChange}
            tab={item}
            setTab={setTab}
            tabsAttr="extraTabs"
            tabIndex={index}
          />
        )
      })),
      {
        id: 'new',
        name: <span style={{ fontSize: '26px', lineHeight: '20px' }}>+</span>
      }
    ]
  }, [embed, onEmbedChange])

  const onTabChange = React.useCallback(
    (newTab: string, tabAttr: 'tabs' | 'extraTabs') => {
      if (newTab === 'new') {
        const newTabId = getUniqueId()

        onEmbedChange?.((embed) => {
          if (embed.type !== 'leveling') return embed
          embed = { ...embed }
          embed[tabAttr] = embed[tabAttr]?.slice() ?? []
          embed[tabAttr]!.push({
            id: newTabId,
            name: ''
          })
          return embed
        })

        setTab(newTabId)
      } else {
        setTab(newTab)
      }
    },
    [onEmbedChange, setTab]
  )

  const onTabClick = React.useCallback(
    (newTabIndex: number) => {
      if (newTabIndex < tabs.length) onTabChange(tabs[newTabIndex].id, 'tabs')
      else onTabChange(extraTabs[newTabIndex - tabs.length].id, 'extraTabs')
    },
    [onTabChange, tabs, extraTabs]
  )

  return (
    <div className={classNames('poe-leveling-viewer', onEmbedChange !== undefined && 'poe-leveling-viewer-editing')}>
      <Tabs
        headersClassName="poe-leveling-tab-headers"
        initialTab={
          activeTabIndex !== undefined
            ? activeTabIndex
            : activeExtraTabIndex !== undefined
            ? tabs.length + activeExtraTabIndex
            : 0
        }
        headerSpacing={{ type: 'fixed', value: onEmbedChange ? 20 : 80 }}
        scrollOnTabChange={{ type: 'center', leftOffset: 0, topOffset: 80 }}
        onTabClick={onTabClick}
        tabs={tabs.map((item, index) => ({
          selector: item.name,
          content: (
            <Tab
              tab={item}
              index={index}
              tabsAttr={'tabs'}
              levelOverride={levelOverride}
              onLevelChange={onLevelChange}
              embed={embed}
              onEmbedChange={onEmbedChange}
              stores={stores}
              metadataId={metadataId}
              isActiveTab={tab === item.id}
              setTab={setTab}
            />
          )
        }))}
        sideTabs={extraTabs.map((item, index) => ({
          selector: item.name,
          content: (
            <Tab
              tab={item}
              index={index}
              tabsAttr={'extraTabs'}
              levelOverride={levelOverride}
              onLevelChange={onLevelChange}
              embed={embed}
              onEmbedChange={onEmbedChange}
              stores={stores}
              metadataId={metadataId}
              isActiveTab={tab === item.id}
              setTab={setTab}
            />
          )
        }))}
      />
    </div>
  )
}
